import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import {
  Copy, Plus, Minus, Download, Upload, Search, Check,
  AlertTriangle, Trash2, RefreshCw, X, Pencil, ChevronLeft, ChevronRight, Layers
} from "lucide-react";
import { getIngredientNamings, updateIngredient, deleteIngredient } from "../api/ingredients";
import { getRMRows } from "../api/Rmdatabase";
import { AuthContext } from "../context/AuthContext";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const FIELDS = [
  { key: "sku", label: "SKU", editable: false, type: "text" },
  { key: "name", label: "Name EN", editable: true, type: "text", required: true },
  { key: "nameFr", label: "Name FR", editable: true, type: "text", required: true },
  { key: "ingredientListEn", label: "Ingredient List EN", editable: true, type: "text" },
  { key: "ingredientListFr", label: "Ingredient List FR", editable: true, type: "text" },
  { key: "unitCost", label: "Raw Cost ($/kg)", editable: true, type: "number" },
  { key: "uom", label: "UOM", editable: true, type: "select", options: ["kg", "g", "ml", "l", "un."] },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const isIncomplete = (row) => !row.name?.trim();
const hasMissingCost = (row) => !row.unitCost || Number(row.unitCost) === 0;

// ─── EXCEL HELPERS ────────────────────────────────────────────────────────────
const EXPORT_KEYS = ["sku", "name", "nameFr", "ingredientListEn", "ingredientListFr", "unitCost", "uom"];

function toExcel(rows) {
  const sheetData = rows.map(r =>
    EXPORT_KEYS.reduce((acc, k) => { acc[k] = r[k] ?? ""; return acc; }, {})
  );
  const ws = XLSX.utils.json_to_sheet(sheetData, { header: EXPORT_KEYS });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Ingredients");
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
}

function parseExcel(buffer) {
  const wb = XLSX.read(buffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

  return rows.map((row, i) => {
    const getValue = (possibleKeys) => {
      const key = Object.keys(row).find((k) =>
        possibleKeys.some((p) => String(k).trim().toLowerCase() === p.toLowerCase())
      );
      return key !== undefined ? row[key] : "";
    };

    const skuVal = String(getValue(["sku", "SKU", "RM Code", "Code", "id"]) || "").trim();
    const nameVal = String(getValue(["name", "nameEn", "NAME EN", "Name EN", "Name", "englishName"]) || "").trim();
    const nameFrVal = String(getValue(["nameFr", "NAME FR", "Name FR", "NameFr", "frenchName"]) || "").trim();
    const ingListEnVal = String(getValue(["ingredientListEn", "INGREDIENT LIST EN", "Ingredient List EN", "ingredientsEn", "ingredients"]) || "").trim();
    const ingListFrVal = String(getValue(["ingredientListFr", "INGREDIENT LIST FR", "Ingredient List FR", "ingredientsFr", "ingrédients"]) || "").trim();
    const costRaw = getValue(["unitCost", "RAW COST ($/KG)", "Raw Cost ($/kg)", "unitCostKgCad", "Raw Cost", "Cost"]);
    const unitCostVal = parseFloat(costRaw) || 0;
    const uomVal = String(getValue(["uom", "UOM"]) || "kg").trim() || "kg";

    return {
      sku: skuVal,
      name: nameVal,
      nameFr: nameFrVal,
      ingredientListEn: ingListEnVal,
      ingredientListFr: ingListFrVal,
      unitCost: unitCostVal,
      uom: uomVal,
      id: skuVal ? skuVal : `imported-${i}-${Date.now()}`,
    };
  }).filter((r) => r.sku || r.name);
}

// ─── CELL ─────────────────────────────────────────────────────────────────────
function Cell({ row, field, onChange, onPaste, isEditMode }) {
  const isEmpty = field.required && !row[field.key]?.toString().trim();
  const isCostMissing = field.key === "unitCost" && hasMissingCost(row);
  const showError = isEmpty || isCostMissing;
  const isEditable = field.editable && isEditMode;

  const base = "w-full px-2 py-1.5 text-sm rounded border outline-none transition-all duration-150 font-mono";
  let cls = base + " ";
  if (!field.editable) cls += "bg-gray-50 text-gray-500 cursor-default border-gray-200";
  else if (!isEditMode) cls += "bg-gray-50 text-gray-700 cursor-default border-gray-200";
  else if (showError) cls += "border-red-400 bg-red-50 text-red-700 focus:border-red-500 focus:ring-1 focus:ring-red-400";
  else cls += "bg-white border-gray-200 focus:border-gray-800 focus:ring-1 focus:ring-gray-800";

  if (field.type === "select") {
    return (
      <select
        className={cls + " cursor-pointer"}
        value={row[field.key] || "kg"}
        onChange={e => isEditable && onChange(row.id, field.key, e.target.value)}
        disabled={!isEditable}
      >
        {field.options.map(o => <option key={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <input
      type={field.type === "number" ? "number" : "text"}
      step={field.type === "number" ? "0.01" : undefined}
      min={field.type === "number" ? "0" : undefined}
      className={cls}
      value={row[field.key] ?? ""}
      readOnly={!isEditable}
      onChange={e => isEditable && onChange(row.id, field.key, e.target.value)}
      onPaste={e => isEditable && onPaste(e, row.id, field.key)}
      placeholder={isEditable ? (field.type === "number" ? "0.00" : `Enter ${field.label}`) : ""}
    />
  );
}

// ─── PAGINATION COMPONENT ─────────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50">
      {/* Left: showing info + page size */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span>
          Showing <strong className="text-gray-700">{startItem}–{endItem}</strong> of <strong className="text-gray-700">{totalItems}</strong>
        </span>
        <div className="flex items-center gap-1.5">
          <span>Per page:</span>
          <select
            value={pageSize}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-gray-800"
          >
            {PAGE_SIZE_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Right: page buttons */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <ChevronLeft size={13} /> Prev
        </button>

        {getPageNumbers().map((page, idx) => (
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-xs">...</span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-8 h-8 text-xs rounded-lg border transition font-medium
                ${currentPage === page
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"}`}
            >
              {page}
            </button>
          )
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Next <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}
// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function IngredientManager() {
  const { user } = useContext(AuthContext);
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";

  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(true);
  const [filterIncomplete, setFilterIncomplete] = useState(false);
  const [copiedCol, setCopiedCol] = useState(null);
  const [dirtyRows, setDirtyRows] = useState(new Set());
  const [savedRows, setSavedRows] = useState(new Set());
  const [editingRows, setEditingRows] = useState(new Set());
  const [rowSnapshots, setRowSnapshots] = useState(new Map());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteConfirmSku, setDeleteConfirmSku] = useState(null);
  const [showBlendModal, setShowBlendModal] = useState(false);
  const [blendRows, setBlendRows] = useState([]);
  const [blendSearch, setBlendSearch] = useState("");
  const [loadingBlends, setLoadingBlends] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fileInputRef = useRef(null);

  // ── Load data on mount ─────────────────────────────────────────────────────
  useEffect(() => {
    if (open && allData.length === 0) {
      loadIngredients();
    }
  }, [open]);

  const handleDeleteRow = (sku) => {
    if (!sku) return;
    setDeleteConfirmSku(sku);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmSku) return;
    const sku = deleteConfirmSku;
    setDeleteConfirmSku(null);
    try {
      await deleteIngredient(sku);
      setAllData(prev => prev.filter(r => r.sku !== sku));
      toast.success("Ingredient deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete ingredient");
    }
  };

  const openBlendImportModal = async () => {
    setShowBlendModal(true);
    setBlendSearch("");
    try {
      setLoadingBlends(true);
      const rows = await getRMRows();
      const blends = (rows || []).filter((r) => {
        const cat = String(r.RMCategory || "").toLowerCase();
        return (
          cat.includes("blend") ||
          cat.includes("mélange") ||
          cat.includes("vrac") ||
          cat.includes("spice")
        );
      });
      setBlendRows(blends);
    } catch (err) {
      toast.error("Failed to load blends from RM Database");
    } finally {
      setLoadingBlends(false);
    }
  };

  const handleSelectBlendToImport = (blend) => {
    const existingIndex = allData.findIndex(
      (r) => String(r.sku).trim() === String(blend.RMCode).trim()
    );

    if (existingIndex >= 0) {
      toast("Blend already exists in list — opening row for editing", {
        icon: "ℹ️",
      });
      const targetId = allData[existingIndex].id;
      setEditingRows((prev) => new Set([...prev, targetId]));
      setDirtyRows((prev) => new Set([...prev, targetId]));
      setShowBlendModal(false);
      return;
    }

    const newId = `imported-${Date.now()}`;
    const newRow = {
      id: newId,
      sku: String(blend.RMCode || "").trim(),
      name: String(blend.RMName || "").trim(),
      nameFr: String(blend.RMName || "").trim(),
      ingredientListEn: "",
      ingredientListFr: "",
      unitCost: Number(blend.TotalRMCost || blend.baseCostKgCad || 0),
      uom: "kg",
    };

    setAllData((prev) => [newRow, ...prev]);
    setDirtyRows((prev) => new Set([...prev, newId]));
    setEditingRows((prev) => new Set([...prev, newId]));
    setCurrentPage(1);
    setShowBlendModal(false);
    toast.success(
      `Imported "${blend.RMName}" — enter bilingual ingredient statements`
    );
  };

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const data = await getIngredientNamings();
      const withIds = data
        .map((item, idx) => ({
          ...item,
          id: item.sku || `row-${idx}`,
          name: item.nameEn || item.name || "",
          nameFr: item.nameFr || "",
          unitCost: item.unitCost || 0,
          uom: item.uom || "kg",
        }))
        .sort((a, b) =>
          String(a.name || a.nameEn || "").localeCompare(
            String(b.name || b.nameEn || ""),
            undefined,
            { sensitivity: "base" }
          )
        );
      setAllData(withIds);
    } catch (err) {
      toast.error("Failed to load ingredients");
    } finally {
      setLoading(false);
    }
  };

  // ── Derived: filtered data (Search by Name or SKU and Sort A-Z) ────────────
  const filteredData = allData
    .filter((row) => {
      const query = search.toLowerCase().trim();
      const sku = String(row.sku || "").toLowerCase();
      const nameEn = String(row.name || row.nameEn || "").toLowerCase();
      const nameFr = String(row.nameFr || "").toLowerCase();
      const matchSearch =
        !query ||
        sku.includes(query) ||
        nameEn.includes(query) ||
        nameFr.includes(query);
      const matchIncomplete = !filterIncomplete || isIncomplete(row);
      return matchSearch && matchIncomplete;
    })
    .sort((a, b) =>
      String(a.name || a.nameEn || "").localeCompare(
        String(b.name || b.nameEn || ""),
        undefined,
        { sensitivity: "base" }
      )
    );

  // ── Reset to page 1 when filter/search changes ─────────────────────────────
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterIncomplete, pageSize]);

  // ── Paginated slice ────────────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const incompleteCount = allData.filter(isIncomplete).length;
  const missingCostCount = allData.filter(hasMissingCost).length;

  // ── Edit / Cancel handlers ─────────────────────────────────────────────────
  const startEditRow = (id) => {
    const target = allData.find((r) => r.id === id);
    if (target) {
      setRowSnapshots((prev) => new Map(prev).set(id, { ...target }));
    }
    setEditingRows((prev) => new Set(prev).add(id));
  };

  const cancelEditRow = (id) => {
    if (rowSnapshots.has(id)) {
      const original = rowSnapshots.get(id);
      setAllData((prev) => prev.map((r) => (r.id === id ? { ...original } : r)));
      setRowSnapshots((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
    setDirtyRows((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setEditingRows((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  // ── Cell change ────────────────────────────────────────────────────────────
  const handleChange = useCallback((id, field, value) => {
    setAllData(prev => prev.map(r => {
      if (r.id === id) {
        return { ...r, [field]: value };
      }
      return r;
    }));
    setDirtyRows(prev => new Set([...prev, id]));
    setSavedRows(prev => { const s = new Set(prev); s.delete(id); return s; });
  }, []);

  // ── Excel paste down column ────────────────────────────────────────────────
  const handlePaste = useCallback((e, rowId, field) => {
    const text = e.clipboardData.getData("text");
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length <= 1) return;
    e.preventDefault();

    setAllData(prev => {
      const startIdx = prev.findIndex(r => r.id === rowId);
      if (startIdx === -1) return prev;
      const updated = [...prev];
      lines.forEach((val, i) => {
        const idx = startIdx + i;
        if (idx < updated.length) {
          const newVal = field === "unitCost" ? parseFloat(val) || 0 : val;
          updated[idx] = {
            ...updated[idx],
            [field]: newVal,
          };
        }
      });
      return updated;
    });

    setDirtyRows(prev => {
      const s = new Set(prev);
      const startIdx = allData.findIndex(r => r.id === rowId);
      lines.forEach((_, i) => { if (allData[startIdx + i]) s.add(allData[startIdx + i].id); });
      return s;
    });

    toast.success(`Pasted ${lines.length} values into column`);
  }, [allData]);

  // ── Copy column ────────────────────────────────────────────────────────────
  const copyColumn = async (field) => {
    const text = allData.map(r => r[field] ?? "").join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCol(field);
      setTimeout(() => setCopiedCol(null), 2000);
      toast.success("Column copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };


  const saveRow = async (id) => {
    const row = allData.find(r => r.id === id);
    if (!row) return;

    try {
      await updateIngredient(row);
      setSavedRows(prev => new Set([...prev, id]));
      setDirtyRows(prev => { const s = new Set(prev); s.delete(id); return s; });
      setEditingRows(prev => { const s = new Set(prev); s.delete(id); return s; });
      setRowSnapshots(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      toast.success("Row saved");
    } catch {
      toast.error("Failed to save row");
    }
  };

  const saveAll = async () => {
    const dirtyRowData = allData.filter(r => dirtyRows.has(r.id));
    const count = dirtyRowData.length;

    try {
      await Promise.all(dirtyRowData.map(row => updateIngredient(row)));
      setSavedRows(prev => new Set([...prev, ...dirtyRows]));
      setDirtyRows(new Set());
      setEditingRows(new Set());
      toast.success(`${count} row${count !== 1 ? "s" : ""} saved`);
    } catch {
      toast.error("Failed to save some rows");
    }
  };

  // ── Add row ────────────────────────────────────────────────────────────────
  const addRow = () => {
    const newId = `new-${Date.now()}`;
    const newRow = { id: newId, sku: "", name: "", unitCost: 0, uom: "kg" };
    setAllData(prev => [newRow, ...prev]);
    setDirtyRows(prev => new Set([...prev, newId]));
    setEditingRows(prev => new Set([...prev, newId]));
    setCurrentPage(1);
    toast("New row added — fill in the details", { icon: "✏️" });
  };

  // ── Refresh ────────────────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const data = await getIngredientNamings();
      const withIds = data.map((item, idx) => ({
        ...item,
        id: item.sku || `row-${idx}`,
        name: item.nameEn || item.name || "",
        nameFr: item.nameFr || "",
        unitCost: item.unitCost || 0,
        uom: item.uom || "kg",
      }));
      setAllData(withIds);
      setDirtyRows(new Set());
      setEditingRows(new Set());
      setSavedRows(new Set());
      setCurrentPage(1);
      toast.success("Ingredients refreshed from database");
    } catch {
      toast.error("Failed to refresh ingredients");
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Export Excel ───────────────────────────────────────────────────────────
  const handleExport = () => {
    const excelBuffer = toExcel(allData);
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ingredients.xlsx";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported ingredients.xlsx");
  };

  // ── Import Excel ───────────────────────────────────────────────────────────
  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const importedRows = parseExcel(new Uint8Array(ev.target.result));
        if (!importedRows.length) {
          toast.error("No valid rows found in Excel file");
          return;
        }

        // 1. Deduplicate incoming Excel rows by SKU first
        const incomingBySku = new Map();
        importedRows.forEach((row) => {
          const key = row.sku ? String(row.sku).trim().toUpperCase() : `NO_SKU_${Math.random()}`;
          if (incomingBySku.has(key)) {
            const existing = incomingBySku.get(key);
            incomingBySku.set(key, { ...existing, ...row, id: existing.id || row.id });
          } else {
            incomingBySku.set(key, row);
          }
        });
        const deduplicatedImportedRows = Array.from(incomingBySku.values());

        // 2. Merge with existing state (prevent duplicate SKUs)
        let mergedList = [];
        setAllData((prev) => {
          mergedList = [...prev];
          deduplicatedImportedRows.forEach((incomingRow) => {
            const normIncomingSku = incomingRow.sku ? String(incomingRow.sku).trim().toUpperCase() : "";
            if (normIncomingSku) {
              const existingIdx = mergedList.findIndex(
                (r) => r.sku && String(r.sku).trim().toUpperCase() === normIncomingSku
              );
              if (existingIdx !== -1) {
                mergedList[existingIdx] = {
                  ...mergedList[existingIdx],
                  ...incomingRow,
                  id: mergedList[existingIdx].id || incomingRow.sku,
                };
              } else {
                mergedList.push({ ...incomingRow, id: incomingRow.sku });
              }
            } else if (incomingRow.name) {
              mergedList.push(incomingRow);
            }
          });
          return mergedList;
        });

        toast.loading("Persisting imported data to database...", { id: "import-toast" });

        // 3. Persist all imported/updated rows with valid SKUs to backend MongoDB
        const rowsToSave = deduplicatedImportedRows.filter((r) => r.sku && String(r.sku).trim().length > 0);
        const results = await Promise.allSettled(rowsToSave.map((r) => updateIngredient(r)));
        
        const successCount = results.filter((res) => res.status === "fulfilled").length;
        const savedIds = new Set(rowsToSave.map((r) => r.sku));

        setSavedRows((prev) => new Set([...prev, ...savedIds]));
        setDirtyRows((prev) => {
          const next = new Set(prev);
          savedIds.forEach((id) => next.delete(id));
          return next;
        });

        toast.success(`Successfully imported & saved ${successCount} row(s) to database!`, { id: "import-toast" });
      } catch (err) {
        console.error("Import error:", err);
        toast.error("Import failed — check Excel format", { id: "import-toast" });
      }
      e.target.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="mb-5 mt-5 font-sans">
      <Toaster position="top-right" reverseOrder={false} />

      {/* ── Accordion Header ── */}
      <div
        className="flex items-center justify-between px-5 py-4 bg-white rounded-xl border border-gray-200 shadow-sm cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Ingredient Manager</h1>
        </div>
        <button className="text-2xl text-gray-500 leading-none">
          {open ? "-" : "+"}
        </button>
      </div>

      {/* ── Content ── */}
      {open && (
        <div className="mt-3 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

          {/* ── Toolbar ── */}
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">

            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or SKU..."
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            <div className="flex-1" />



            {/* Add Row */}
            {/* <button
              onClick={addRow}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition"
            >
              <Plus size={15} /> Add Row
            </button> */}

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition"
            >
              <RefreshCw size={15} className={isRefreshing ? "animate-spin" : ""} />
              Refresh
            </button>

            {/* Export */}
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition"
            >
              <Download size={15} /> Export
            </button>

            {/* Import Blend from RM */}
            <button
              onClick={openBlendImportModal}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition shadow-sm"
              title="Import an existing blend recipe from RM Database to configure bilingual ingredients"
            >
              <Layers size={15} /> Import Blend
            </button>

            {/* Import */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition"
            >
              <Upload size={15} /> Import
            </button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileImport} />
          </div>

          {/* ── Table ── */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {FIELDS.map(f => (
                    <th key={f.key} className="px-3 py-3 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                          {f.label}
                          {f.required && <span className="text-red-400 ml-0.5">*</span>}
                        </span>
                        <button
                          onClick={() => copyColumn(f.key)}
                          className="p-0.5 rounded hover:bg-gray-200 transition shrink-0"
                          title={`Copy ${f.label} column`}
                        >
                          {copiedCol === f.key
                            ? <Check size={12} className="text-green-600" />
                            : <Copy size={12} className="text-gray-400" />}
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-center whitespace-nowrap">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</span>
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* Loading state */}
                {loading && (
                  <tr>
                    <td colSpan={FIELDS.length + 1} className="px-4 py-14 text-center">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <RefreshCw size={24} className="animate-spin" />
                        <span className="text-sm">Loading ingredients...</span>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Empty state */}
                {!loading && paginatedData.length === 0 && (
                  <tr>
                    <td colSpan={FIELDS.length + 1} className="px-4 py-14 text-center text-gray-400 text-sm">
                      {filterIncomplete ? "No incomplete ingredients 🎉" : "No ingredients found"}
                    </td>
                  </tr>
                )}

                {!loading && paginatedData.map((row, idx) => {
                  const incomplete = isIncomplete(row);
                  const isDirty = dirtyRows.has(row.id);
                  const isSaved = savedRows.has(row.id);
                  const isEditing = editingRows.has(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={`border-b border-gray-100 transition-colors
                        ${incomplete ? "bg-amber-50/40" : idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}
                        hover:bg-blue-50/20`}
                    >
                      {FIELDS.map(f => (
                        <td key={f.key} className="px-3 py-2">
                          <Cell
                            row={row}
                            field={f}
                            onChange={handleChange}
                            onPaste={handlePaste}
                            isEditMode={isEditing}
                          />
                        </td>
                      ))}

                      {/* ── Actions ── */}
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-1.5">

                          {/* Edit / Cancel toggle */}
                          {isEditing ? (
                            <button
                              onClick={() => cancelEditRow(row.id)}
                              title="Cancel edit"
                              className="p-2 rounded-lg bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 hover:text-black transition"
                            >
                              <X size={13} />
                            </button>
                          ) : (
                            <button
                              onClick={() => startEditRow(row.id)}
                              title="Edit row"
                              className="p-2 rounded-lg bg-white text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-gray-800 transition"
                            >
                              <Pencil size={13} />
                            </button>
                          )}

                          {/* Save row (only when dirty) */}
                          {isDirty && (
                            <button
                              onClick={() => saveRow(row.id)}
                              title="Save row"
                              className="p-2 rounded-lg bg-black text-white border border-black hover:bg-gray-800 transition"
                            >
                              <Check size={13} />
                            </button>
                          )}

                          {/* Delete row (Admin only) */}
                          {isAdmin && row.sku && (
                            <button
                              onClick={() => handleDeleteRow(row.sku)}
                              title="Delete ingredient"
                              className="p-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700 transition"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}


                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {!loading && filteredData.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredData.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          )}

          {/* ── Footer ── */}
          <div className="flex items-center justify-center px-4 py-2.5 border-t border-gray-100  text-center bg-gray-50 text-xs text-gray-500">
            <span>
              {filteredData.length !== allData.length && (
                <>Filtered <strong>{filteredData.length}</strong> of </>
              )}
              {/* <strong>{allData.length}</strong> total ingredients */}
              {filterIncomplete && (
                <> · <span className="text-gray-700 font-semibold">Incomplete filter active</span></>
              )}
            </span>
            <span className="hidden sm:block text-center justify-center items-center ">
              Click <Pencil size={14} className="inline mb-0.5 text-center" /> to edit · Paste from Excel to fill column
            </span>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteConfirmSku && (
        <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-auto shadow-2xl border border-gray-100 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600 mb-4">
              <Trash2 size={24} />
            </div>
            
            <h3 className="text-lg font-bold text-gray-900 mb-1">Delete Ingredient</h3>
            <p className="text-sm text-gray-500 mb-6 font-sans">
              Are you sure you want to delete ingredient with SKU <strong className="text-gray-950 font-semibold">{deleteConfirmSku}</strong>? This action cannot be undone.
            </p>

            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={() => setDeleteConfirmSku(null)}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Blend Modal ── */}
      {showBlendModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-sans">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Blend from RM Database</h3>
                <p className="text-xs text-gray-500">
                  Select a blend recipe to auto-populate SKU, name, and cost for ingredient list configuration
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBlendModal(false)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Search */}
            <div className="pt-4 pb-3">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search blends by SKU or name..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={blendSearch}
                  onChange={(e) => setBlendSearch(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto min-h-[200px] divide-y divide-gray-100">
              {loadingBlends ? (
                <div className="py-12 text-center text-sm text-gray-500">Loading blends from RM Database...</div>
              ) : blendRows.filter((r) => {
                  const q = blendSearch.toLowerCase().trim();
                  return (
                    !q ||
                    String(r.RMCode).toLowerCase().includes(q) ||
                    String(r.RMName).toLowerCase().includes(q) ||
                    String(r.RMCategory).toLowerCase().includes(q)
                  );
                }).length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">No matching blends found in RM Database.</div>
              ) : (
                blendRows
                  .filter((r) => {
                    const q = blendSearch.toLowerCase().trim();
                    return (
                      !q ||
                      String(r.RMCode).toLowerCase().includes(q) ||
                      String(r.RMName).toLowerCase().includes(q) ||
                      String(r.RMCategory).toLowerCase().includes(q)
                    );
                  })
                  .map((blend) => (
                    <div
                      key={blend._id || blend.RMCode}
                      className="flex items-center justify-between p-3 hover:bg-indigo-50/50 rounded-lg transition cursor-pointer"
                      onClick={() => handleSelectBlendToImport(blend)}
                    >
                      <div className="min-w-0 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold px-2 py-0.5 bg-gray-100 text-gray-800 rounded">
                            {blend.RMCode}
                          </span>
                          <span className="font-medium text-sm text-gray-900 truncate">
                            {blend.RMName}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex gap-2">
                          <span>{blend.RMCategory}</span>
                          <span>•</span>
                          <span>Base: ${Number(blend.baseCostKgCad || 0).toFixed(2)}/kg</span>
                          <span>•</span>
                          <span>Total: ${Number(blend.TotalRMCost || blend.baseCostKgCad || 0).toFixed(2)}/kg</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectBlendToImport(blend);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition shrink-0"
                      >
                        Select
                      </button>
                    </div>
                  ))
              )}
            </div>

            <div className="flex justify-end pt-3 mt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowBlendModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
